/**
 * Capability profile helpers — mirrors backend agentShellCodes.
 * Prefer bound tool handler_refs; agent code is bootstrap only when tools are empty.
 */

export type AgentCapabilityProfile = 'decision' | 'scheduling' | 'quality_control';

const SCHEDULING_HANDLER_MARKERS = [
  'run.run_compute',
  'run.select_plan',
  'run.commit_plan',
  'run.get_resource_map',
  'run.get_current_plan',
] as const;

const QC_HANDLER_MARKERS = [
  'run.get_quality_snapshot',
  'run.get_open_ncrs',
  'run.record_quality_event',
  'run.update_quality_context',
] as const;

export function normalizeAgentCode(code: string | null | undefined): string {
  return (code || '').trim().toUpperCase();
}

/** Dynamic Scheduling seed codes and renamed copies (DYNAMIC_SCHEDULING_V2, …). */
export function isDynamicSchedulingAgentCode(code: string | null | undefined): boolean {
  const c = normalizeAgentCode(code);
  return c === 'DYNAMIC_SCHEDULING' || c.startsWith('DYNAMIC_SCHEDULING_');
}

export function isQualityControlFloorAgentCode(code: string | null | undefined): boolean {
  const c = normalizeAgentCode(code);
  return c === 'QUALITY_CONTROL_FLOOR' || c.startsWith('QUALITY_CONTROL_FLOOR_');
}

export function resolveCapabilityProfile(input: {
  handlerRefs?: string[] | null;
  computeRefs?: string[] | null;
  agentCode?: string | null;
}): AgentCapabilityProfile {
  const handlers = new Set(
    (input.handlerRefs ?? []).map((h) => h.trim()).filter(Boolean)
  );
  const computeRefs = (input.computeRefs ?? [])
    .map((r) => r.trim().toLowerCase())
    .filter(Boolean);

  if (
    SCHEDULING_HANDLER_MARKERS.some((m) => handlers.has(m)) ||
    computeRefs.some((r) => r.startsWith('sched.'))
  ) {
    return 'scheduling';
  }
  if (QC_HANDLER_MARKERS.some((m) => handlers.has(m))) {
    return 'quality_control';
  }
  if (handlers.size === 0 && computeRefs.length === 0) {
    if (isDynamicSchedulingAgentCode(input.agentCode)) return 'scheduling';
    if (isQualityControlFloorAgentCode(input.agentCode)) return 'quality_control';
  }
  return 'decision';
}

export function isSchedulingCapable(input: {
  handlerRefs?: string[] | null;
  computeRefs?: string[] | null;
  agentCode?: string | null;
}): boolean {
  return resolveCapabilityProfile(input) === 'scheduling';
}
